import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { activityNeutralButtonClass, activitySearchInputClass } from 'features/activity/styles/dataTableStyles';

const W = 320;

/** The bulk bar's Merge: a searchable list of issues to fold the picked ones into. */
export default function MergeTarget({ options, onPick, disabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const r = ref.current?.getBoundingClientRect();
      if (!r) return;
      const w = Math.min(W, window.innerWidth - 16);
      setPos({ bottom: window.innerHeight - r.top + 4, left: Math.min(Math.max(8, r.left), window.innerWidth - w - 8), width: w });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open]);

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? options.filter((o) => String(o.title).toLowerCase().includes(s) || `#${o.id}` === s) : options;
  }, [options, q]);

  return (
    <div ref={ref} className="relative">
      <button type="button" className={activityNeutralButtonClass} disabled={disabled || !options.length}
        onClick={() => { setQ(''); setOpen((o) => !o); }}>Merge into…</button>
      {open && pos ? createPortal(
        <div ref={menuRef} role="menu" aria-label="Merge into"
          className="rounded-md border border-[color:var(--border-default)] bg-surface-raised shadow-token-md"
          style={{ position: 'fixed', bottom: pos.bottom, left: pos.left, width: pos.width, zIndex: 1400 }}>
          <div className="border-b border-[color:var(--border-default)] px-3 py-2">
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search issues…" aria-label="Search issues" className={activitySearchInputClass} />
          </div>
          <div className="max-h-64 overflow-y-auto p-1">
            {shown.map((o) => (
              <button key={o.id} type="button" role="menuitem" onClick={() => { setOpen(false); onPick(o); }}
                className="flex w-full flex-col items-start rounded px-3 py-1.5 text-left hover:bg-surface-hover">
                <span className="w-full truncate text-[12px] text-[color:var(--text-primary)]">{o.title}</span>
                <span className="text-[11px] text-[color:var(--text-muted)]">{o.agent_name} · #{o.id}</span>
              </button>
            ))}
            {shown.length === 0 && <div className="px-3 py-2 text-[12px] text-[color:var(--text-muted)]">No issues match.</div>}
          </div>
        </div>,
        document.body,
      ) : null}
    </div>
  );
}
